
import React from "react";
import { MaskedInput, MaskedInputProps } from "./masked-input";

export type MaskType = "phone" | "cnpj" | "cpf" | "cep" | "date";

const MASK_PATTERNS = {
  phone: "(99) 99999-9999",
  cnpj: "99.999.999/9999-99",
  cpf: "999.999.999-99",
  cep: "99999-999",
  date: "99/99/9999"
};

const PLACEHOLDERS = {
  phone: "(00) 00000-0000",
  cnpj: "00.000.000/0000-00",
  cpf: "000.000.000-00",
  cep: "00000-000",
  date: "DD/MM/YYYY"
};

interface TypedMaskedInputProps extends Omit<MaskedInputProps, "mask" | "placeholder"> {
  maskType: MaskType;
  placeholder?: string;
}

const createMaskedInput = (maskType: MaskType, defaultProps?: Partial<MaskedInputProps>) => {
  const Component = React.forwardRef<HTMLInputElement, Omit<TypedMaskedInputProps, "maskType"> & { maskType?: MaskType }>(
    ({ maskType: providedType, placeholder, ...props }, ref) => {
      const type = providedType || maskType;
      return (
        <MaskedInput
          mask={MASK_PATTERNS[type]}
          placeholder={placeholder || PLACEHOLDERS[type]}
          {...defaultProps}
          {...props}
          ref={ref}
        />
      );
    }
  );
  
  Component.displayName = `${maskType.charAt(0).toUpperCase() + maskType.slice(1)}Input`;
  return Component;
};

export const PhoneInput = createMaskedInput("phone");
export const CNPJInput = createMaskedInput("cnpj");
export const CPFInput = createMaskedInput("cpf");
export const CEPInput = createMaskedInput("cep");
export const DateInput = createMaskedInput("date");

// Generic component that can be used with any mask type
export const TypedMaskedInput = React.forwardRef<HTMLInputElement, TypedMaskedInputProps>(
  ({ maskType, ...props }, ref) => {
    return (
      <MaskedInput
        mask={MASK_PATTERNS[maskType]}
        placeholder={props.placeholder || PLACEHOLDERS[maskType]}
        {...props}
        ref={ref}
      />
    );
  }
);

TypedMaskedInput.displayName = "TypedMaskedInput";
